import React, { useState, useEffect } from "react";
import AuthLayout from "./AuthLayout";
import DropCatchModal from "./DropCatchModal";
import { signUp } from "../../lib/auth-client";

interface SignupPageProps {
  onNavigate: (page: "login" | "verify-email") => void;
  onSuccess: (email: string) => void;
}

const SignupPage: React.FC<SignupPageProps> = ({ onNavigate, onSuccess }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [hasDropCatch, setHasDropCatch] = useState(false);
  const [dropCatchClientId, setDropCatchClientId] = useState("");
  const [dropCatchClientSecret, setDropCatchClientSecret] = useState("");
  const [showDropCatchModal, setShowDropCatchModal] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Calculate password strength
  useEffect(() => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(Math.min(strength, 5));
  }, [password]);

  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return { label: "WEAK", color: "text-red-500" };
    if (passwordStrength <= 2) return { label: "FAIR", color: "text-yellow-500" };
    if (passwordStrength <= 3) return { label: "GOOD", color: "text-yellow-400" };
    if (passwordStrength <= 4) return { label: "STRONG", color: "text-green-400" };
    return { label: "EXCELLENT", color: "text-green-500" };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!fullName || !email || !password || !confirmPassword) {
      setError("ERROR: ALL FIELDS REQUIRED");
      return;
    }

    if (password !== confirmPassword) {
      setError("ERROR: PASSWORDS DO NOT MATCH");
      return;
    }

    if (password.length < 8) {
      setError("ERROR: PASSWORD MUST BE AT LEAST 8 CHARACTERS");
      return;
    }

    setLoading(true);

    try {
      const result = await signUp.email({
        email,
        password,
        name: fullName,
      });

      if (result.error) {
        setError(`ERROR: ${result.error.message?.toUpperCase() || "REGISTRATION FAILED"}`);
      } else {
        // If user provided DropCatch credentials, save them
        if (hasDropCatch && dropCatchClientId && dropCatchClientSecret) {
          try {
            await fetch("/api/user/config", {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                dropcatch_client_id: dropCatchClientId,
                dropcatch_client_secret: dropCatchClientSecret,
              }),
            });
          } catch {
            // Config save failed, but account was created
            console.error("Failed to save DropCatch credentials");
          }
        }
        onSuccess(email);
      }
    } catch (err) {
      setError("ERROR: CONNECTION FAILED. TRY AGAIN.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="*** CREATE ACCOUNT ***">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            FULL NAME:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="JOHN DOE"
              autoFocus
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            EMAIL:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="user@domain.com"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            PASSWORD:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="********"
            />
          </div>
          {password && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1 bg-green-900 rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    passwordStrength <= 1 ? "bg-red-500" :
                    passwordStrength <= 2 ? "bg-yellow-500" :
                    passwordStrength <= 3 ? "bg-yellow-400" :
                    "bg-green-500"
                  }`}
                  style={{ width: `${(passwordStrength / 5) * 100}%` }}
                />
              </div>
              <span className={`text-xs ${getStrengthLabel().color}`}>
                {getStrengthLabel().label}
              </span>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-green-500 text-sm mb-2 tracking-wider">
            CONFIRM PASSWORD:
          </label>
          <div className="flex items-center border-2 border-green-700 focus-within:border-green-500 bg-black">
            <span className="px-3 text-green-600">{">"}</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="flex-1 px-2 py-3 bg-transparent text-green-400 outline-none tracking-wider"
              placeholder="********"
            />
          </div>
          {confirmPassword && password !== confirmPassword && (
            <div className="text-red-400 text-xs mt-1">PASSWORDS DO NOT MATCH</div>
          )}
        </div>

        {/* DropCatch Toggle */}
        <div className="border-2 border-green-700 p-4">
          <div className="flex items-center gap-3 mb-3">
            <input
              type="checkbox"
              id="hasDropCatch"
              checked={hasDropCatch}
              onChange={(e) => setHasDropCatch(e.target.checked)}
              className="accent-green-500 w-4 h-4"
            />
            <label htmlFor="hasDropCatch" className="text-green-500 text-sm cursor-pointer">
              I HAVE DROPCATCH API CREDENTIALS
            </label>
          </div>

          {hasDropCatch ? (
            <div className="space-y-3 animate-fadeIn">
              <div>
                <label className="block text-green-600 text-xs mb-1">CLIENT ID:</label>
                <input
                  type="text"
                  value={dropCatchClientId}
                  onChange={(e) => setDropCatchClientId(e.target.value)}
                  className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 outline-none focus:border-green-500 text-sm"
                  placeholder="Enter Client ID"
                />
              </div>
              <div>
                <label className="block text-green-600 text-xs mb-1">CLIENT SECRET:</label>
                <input
                  type="password"
                  value={dropCatchClientSecret}
                  onChange={(e) => setDropCatchClientSecret(e.target.value)}
                  className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 outline-none focus:border-green-500 text-sm"
                  placeholder="Enter Client Secret"
                />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDropCatchModal(true)}
              className="text-green-600 hover:text-green-400 text-sm underline"
            >
              DON'T HAVE CREDENTIALS? CLICK HERE FOR INSTRUCTIONS
            </button>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div
            className="border-2 border-red-500 bg-black p-3 text-center"
            style={{ animation: "shake 0.5s ease-in-out" }}
          >
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-green-500 text-black font-bold tracking-widest hover:bg-green-400 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ boxShadow: "0 0 20px rgba(34, 197, 94, 0.4)" }}
        >
          {loading ? "[CREATING ACCOUNT...]" : "[CREATE ACCOUNT]"}
        </button>

        {/* Login link */}
        <div className="text-center">
          <span className="text-green-700 text-sm">ALREADY HAVE AN ACCOUNT? </span>
          <button
            type="button"
            onClick={() => onNavigate("login")}
            className="text-green-500 hover:text-green-400 text-sm underline"
          >
            LOGIN
          </button>
        </div>
      </form>

      {/* DropCatch Modal */}
      {showDropCatchModal && (
        <DropCatchModal
          onClose={() => setShowDropCatchModal(false)}
          onHaveCredentials={() => {
            setHasDropCatch(true);
            setShowDropCatchModal(false);
          }}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </AuthLayout>
  );
};

export default SignupPage;
